import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  Modal,
  PageHead,
  Select,
  SectionHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { useAuth } from '@/auth/AuthContext'
import { useClubContext, useConfigQuery } from '@/context/ClubContext'
import { Cur } from '@/lib/currency'
import { count, dateInputToMs, dateTime, money, msToDateInput } from '@/lib/format'

const DAY_MS = 86_400_000

// ─────────────────────────── Типы ответов API ───────────────────────────

interface SmsTotals {
  messages: number
  sent: number
  failed: number
  dryRun: number
  segments: number
  billableSegments: number
}

interface KindRow extends SmsTotals {
  kind: string
  label: string
}

interface DayRow {
  day: string
  messages: number
  sent: number
  failed: number
  billableSegments: number
}

interface ClubSummary {
  from: number
  to: number
  totals: SmsTotals
  byKind: KindRow[]
  byDay: DayRow[]
  currentMonth: {
    year: number
    month: number
    billableSegments: number
    freeSegments: number
    chargedSegments: number
    pricePerSegment: number
    amount: number
  }
}

interface ClubOverviewRow extends SmsTotals {
  clubId: string
  clubName: string
  isTest: boolean
  monthBillableSegments: number
  freeSegments: number
  chargedSegments: number
  amount: number
}

interface PlatformOverview {
  from: number
  to: number
  totals: SmsTotals
  monthAmount: number
  byKind: KindRow[]
  clubs: ClubOverviewRow[]
}

interface LogItem {
  id: string
  kind: string
  phone: string
  body: string | null
  chars: number
  segments: number
  billableSegments: number
  status: 'sent' | 'failed' | 'dry_run'
  providerCode: string | null
  error: string | null
  campaignId: string | null
  createdAt: number
}

interface EffectiveLimits {
  freeSegments: number
  pricePerSegment: number
  campaignMaxRecipients: number
  campaignsEnabled: boolean
  hasOverride: boolean
  overridden: string[]
}

interface MonthUsage {
  year: number
  month: number
  billableSegments: number
  freeSegments: number
  chargedSegments: number
  pricePerSegment: number
  amount: number
  charged: boolean
  open: boolean
}

interface UsageResponse {
  limits: EffectiveLimits
  months: MonthUsage[]
  pending: MonthUsage[]
  pendingAmount: number
}

interface Campaign {
  id: string
  title: string
  body: string
  status: 'pending' | 'running' | 'done' | 'cancelled' | 'failed'
  segmentsPerMessage: number
  total: number
  sent: number
  failed: number
  skipped: number
  error: string | null
  createdAt: number
  finishedAt: number | null
}

interface CampaignsResponse {
  items: Campaign[]
  limits: EffectiveLimits
  audienceSize: number
}

interface CampaignPreview {
  recipients: number
  skippedOptOut: number
  skippedNoPhone: number
  segmentsPerMessage: number
  totalSegments: number
  chargedSegments: number
  estimatedCost: number
  freeRemaining: number
  pricePerSegment: number
  maxRecipients: number
  preparedBody: string
}

interface OptOut {
  id: string
  phone: string
  note: string | null
  createdAt: number
}

interface BillingDefaults {
  freeSegments: number
  pricePerSegment: number
  campaignMaxRecipients: number
  updatedAt: number
}

interface ClubLimitsResponse {
  override: {
    freeSegments: number | null
    pricePerSegment: number | null
    campaignMaxRecipients: number | null
    campaignsEnabled: boolean
  } | null
  effective: EffectiveLimits
}

interface LoyaltyLevel {
  id: string
  name: string
}

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь'
]

function monthLabel(m: MonthUsage): string {
  return `${MONTHS[m.month - 1] ?? m.month} ${m.year}`
}

const STATUS_LABEL: Record<LogItem['status'], string> = {
  sent: 'Отправлено',
  failed: 'Ошибка',
  dry_run: 'Тестовый режим'
}

function statusColor(status: LogItem['status']): string {
  if (status === 'sent') return 'var(--green)'
  if (status === 'failed') return 'var(--red)'
  return 'var(--amber)'
}

type Tab = 'summary' | 'log' | 'campaigns' | 'test' | 'overview' | 'pricing'

const TAB_LEAD: Record<Tab, string> = {
  summary:
    'Сколько SMS ушло у клуба, из чего складывается расход и сколько осталось от бесплатного порога месяца.',
  log: 'Все попытки отправки: кому, когда, чем закончилось и что ответил шлюз.',
  campaigns: 'Рассылки об акциях по собственной базе клиентов и стоп-лист отписавшихся.',
  test: 'Проверочная отправка на свой номер: проверить логин, пароль и IP-доступ к шлюзу.',
  overview: 'Отправленные SMS по всем подключённым заведениям и расход текущего месяца.',
  pricing: 'Бесплатный порог и цена сообщения сверх него — общая и по конкретному клубу.'
}

const TAB_TITLE: Record<Tab, string> = {
  summary: 'Сводка',
  log: 'Журнал отправок',
  campaigns: 'Рассылки',
  test: 'Проверка шлюза',
  overview: 'Все заведения',
  pricing: 'Тарификация'
}

/**
 * Учёт SMS: сводка и журнал по клубу, массовые рассылки, проверка шлюза,
 * а для администратора платформы — картина по всем заведениям и прейскурант.
 */
export function Sms(): React.JSX.Element {
  const { hasRole } = useAuth()
  const isSuperAdmin = hasRole('super_admin')
  // Бухгалтеру нужен расход (он попадает в счёт), но не рассылки и не отправка
  // проверочных SMS — эти эндпоинты для его роли всё равно закрыты на бэкенде.
  const isAccountant = hasRole('accountant')
  const [tab, setTab] = useState<Tab>(isSuperAdmin ? 'overview' : 'summary')

  const tabs: Tab[] = isSuperAdmin
    ? ['overview', 'pricing', 'summary', 'log', 'campaigns', 'test']
    : isAccountant
      ? ['summary', 'log']
      : ['summary', 'log', 'campaigns', 'test']

  return (
    <>
      <PageHead eyebrow="SMS" title={TAB_TITLE[tab]} lead={TAB_LEAD[tab]} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button
            key={t}
            className={tab === t ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setTab(t)}
          >
            {TAB_TITLE[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && <PlatformOverviewTool />}
      {tab === 'pricing' && <PricingTool />}
      {tab === 'summary' && (
        <ClubGate>
          <SummaryTool />
        </ClubGate>
      )}
      {tab === 'log' && (
        <ClubGate>
          <LogTool />
        </ClubGate>
      )}
      {tab === 'campaigns' && (
        <ClubGate>
          <CampaignsTool />
        </ClubGate>
      )}
      {tab === 'test' && (
        <ClubGate>
          <SelfTestTool />
        </ClubGate>
      )}
    </>
  )
}

// ═══════════════════════════ Период ═══════════════════════════

/** Выбор периода, общий для сводки, журнала и обзора платформы. */
function usePeriod(): { from: number; to: number; controls: React.JSX.Element } {
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))

  const from = dateInputToMs(fromStr)
  // Конец дня «по» включительно.
  const to = dateInputToMs(toStr) + DAY_MS

  const controls = (
    <>
      <Field label="С даты" htmlFor="sms-from">
        <Input
          id="sms-from"
          type="date"
          value={fromStr}
          max={toStr}
          onChange={(e) => setFromStr(e.target.value)}
          required
        />
      </Field>
      <Field label="По дату" htmlFor="sms-to">
        <Input
          id="sms-to"
          type="date"
          value={toStr}
          min={fromStr}
          onChange={(e) => setToStr(e.target.value)}
          required
        />
      </Field>
    </>
  )

  return { from, to, controls }
}

/** Итоговые KPI — одинаковые в клубной сводке и в обзоре платформы. */
function TotalsKpi({ totals }: { totals: SmsTotals }): React.JSX.Element {
  return (
    <div className="kpi-grid">
      <Kpi label="Всего сообщений" value={count(totals.messages)} />
      <Kpi
        label="Успешно"
        value={<span style={{ color: 'var(--green)' }}>{count(totals.sent)}</span>}
        sub={totals.messages ? `${Math.round((totals.sent / totals.messages) * 100)} %` : undefined}
      />
      <Kpi
        label="Неуспешно"
        value={
          <span style={{ color: totals.failed ? 'var(--red)' : undefined }}>
            {count(totals.failed)}
          </span>
        }
      />
      <Kpi
        label="Сегментов"
        value={count(totals.segments)}
        sub={`в тарификацию: ${count(totals.billableSegments)}`}
      />
      {totals.dryRun > 0 && (
        <Kpi
          label="Тестовый режим"
          value={<span style={{ color: 'var(--amber)' }}>{count(totals.dryRun)}</span>}
          sub="реальной отправки не было"
        />
      )}
    </div>
  )
}

/** Разбивка по видам сообщений — за что именно клуб платит. */
function KindTable({ rows }: { rows: KindRow[] }): React.JSX.Element {
  if (rows.length === 0) return <EmptyState message="За период сообщений не было." />
  const totalBillable = rows.reduce((s, r) => s + r.billableSegments, 0)
  return (
    <div className="table-wrap">
      <table className="rtable">
        <thead>
          <tr>
            <th>Вид сообщений</th>
            <th className="num">Всего</th>
            <th className="num">Успешно</th>
            <th className="num">Ошибок</th>
            <th className="num">Сегментов</th>
            <th className="num">Доля расхода</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.kind}>
              <td>{r.label}</td>
              <td className="num">{count(r.messages)}</td>
              <td className="num">{count(r.sent)}</td>
              <td className="num">
                {r.failed > 0 ? (
                  <span style={{ color: 'var(--red)' }}>{count(r.failed)}</span>
                ) : (
                  '—'
                )}
              </td>
              <td className="num">{count(r.billableSegments)}</td>
              <td className="num">
                {totalBillable
                  ? `${Math.round((r.billableSegments / totalBillable) * 100)} %`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ Сводка клуба ═══════════════════════════

function SummaryTool(): React.JSX.Element {
  const query = useConfigQuery()
  const period = usePeriod()
  const clubId = (query?.clubId as string | undefined) ?? null

  const summary = useFetch<ClubSummary>('/v1/sms/stats', { ...query, from: period.from, to: period.to }, [
    clubId,
    period.from,
    period.to
  ])
  const usage = useFetch<UsageResponse>('/v1/sms/usage', query, [clubId])

  const maxDay = useMemo(
    () => Math.max(1, ...(summary.data?.byDay ?? []).map((d) => d.messages)),
    [summary.data]
  )

  return (
    <>
      <div className="card">
        <SectionHead title="Период" />
        <form className="report-form" onSubmit={(e) => e.preventDefault()}>
          {period.controls}
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <SectionHead title="Итоги за период" />
        <AsyncState query={summary} />
        {summary.data && (
          <>
            <TotalsKpi totals={summary.data.totals} />
            <div style={{ marginTop: 24 }}>
              <SectionHead title="По видам сообщений" />
              <KindTable rows={summary.data.byKind} />
            </div>
          </>
        )}
      </div>

      {summary.data && summary.data.byDay.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <SectionHead title="По дням" />
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>День</th>
                  <th className="num">Всего</th>
                  <th className="num">Успешно</th>
                  <th className="num">Ошибок</th>
                  <th className="num">Сегментов</th>
                  <th style={{ width: '30%' }} />
                </tr>
              </thead>
              <tbody>
                {summary.data.byDay.map((d) => (
                  <tr key={d.day}>
                    <td>{d.day}</td>
                    <td className="num">{count(d.messages)}</td>
                    <td className="num">{count(d.sent)}</td>
                    <td className="num">
                      {d.failed > 0 ? (
                        <span style={{ color: 'var(--red)' }}>{count(d.failed)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">{count(d.billableSegments)}</td>
                    <td>
                      {/* Полоска вместо графика: тренд читается сразу, а библиотека
                          графиков ради одной таблицы в бэкофис не тянется. */}
                      <div
                        style={{
                          height: 8,
                          borderRadius: 4,
                          background: 'var(--accent)',
                          opacity: 0.75,
                          width: `${(d.messages / maxDay) * 100}%`,
                          minWidth: 2
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UsageCard usage={usage} />
    </>
  )
}

/** Расход против порога: текущий месяц, история и что уйдёт в счёт. */
function UsageCard({
  usage
}: {
  usage: { data: UsageResponse | null; loading: boolean; error: string | null }
}): React.JSX.Element {
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead
        title="Расход и бесплатный порог"
        meta={
          usage.data
            ? `${count(usage.data.limits.freeSegments)} сегм./мес. включено, далее ${money(usage.data.limits.pricePerSegment)} за сегмент`
            : undefined
        }
      />
      <AsyncState query={usage} />
      {usage.data && (
        <>
          {usage.data.pending.length > 0 && (
            <div className="state state--error" role="status" style={{ marginBottom: 16 }}>
              <span>⚠</span>
              <span>
                Превышение за {usage.data.pending.map((m) => monthLabel(m)).join(', ')} на сумму{' '}
                <strong>
                  {money(usage.data.pendingAmount)} <Cur />
                </strong>{' '}
                будет включено в ближайший счёт.
              </span>
            </div>
          )}
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Месяц</th>
                  <th className="num">Сегментов</th>
                  <th className="num">Включено</th>
                  <th className="num">Сверх порога</th>
                  <th className="num">К оплате</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {usage.data.months.map((m) => (
                  <tr key={`${m.year}-${m.month}`}>
                    <td>{monthLabel(m)}</td>
                    <td className="num">{count(m.billableSegments)}</td>
                    <td className="num">{count(m.freeSegments)}</td>
                    <td className="num">
                      {m.chargedSegments > 0 ? (
                        <span style={{ color: 'var(--amber)' }}>{count(m.chargedSegments)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">
                      {m.amount > 0 ? (
                        <>
                          {money(m.amount)} <Cur />
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {m.open ? (
                        <span className="muted">месяц идёт</span>
                      ) : m.charged ? (
                        <span style={{ color: 'var(--green)' }}>в счёте</span>
                      ) : m.amount > 0 ? (
                        <span style={{ color: 'var(--amber)' }}>к выставлению</span>
                      ) : (
                        <span className="muted">в пределах порога</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ═══════════════════════════ Журнал ═══════════════════════════

const PAGE_SIZE = 50

function LogTool(): React.JSX.Element {
  const query = useConfigQuery()
  const clubId = (query?.clubId as string | undefined) ?? null
  const period = usePeriod()
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(0)

  // Смена фильтров возвращает на первую страницу: иначе пустой «хвост» списка
  // выглядит как «данных нет».
  useEffect(() => setPage(0), [kind, status, period.from, period.to, clubId])

  const log = useFetch<{ total: number; items: LogItem[] }>(
    '/v1/sms/log',
    {
      ...query,
      from: period.from,
      to: period.to,
      kind: kind || undefined,
      status: status || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE
    },
    [clubId, period.from, period.to, kind, status, page]
  )

  const total = log.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="card">
      <SectionHead title="Журнал отправок" meta={total ? `${count(total)} записей` : undefined} />

      <form className="report-form" onSubmit={(e) => e.preventDefault()}>
        {period.controls}
        <Field label="Вид" htmlFor="sms-kind">
          <Select id="sms-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Все</option>
            <option value="otp">Коды подтверждения</option>
            <option value="booking_confirm">Подтверждения брони</option>
            <option value="campaign">Массовые рассылки</option>
            <option value="test">Проверки шлюза</option>
            <option value="other">Прочее</option>
          </Select>
        </Field>
        <Field label="Результат" htmlFor="sms-status">
          <Select id="sms-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Любой</option>
            <option value="sent">Отправлено</option>
            <option value="failed">Ошибка</option>
            <option value="dry_run">Тестовый режим</option>
          </Select>
        </Field>
      </form>

      <AsyncState query={log} isEmpty={total === 0} empty="За период отправок не было." />

      {log.data && log.data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Номер</th>
                  <th>Вид</th>
                  <th>Текст</th>
                  <th className="num">Сегм.</th>
                  <th>Результат</th>
                </tr>
              </thead>
              <tbody>
                {log.data.items.map((r) => (
                  <tr key={r.id}>
                    <td>{dateTime(r.createdAt)}</td>
                    <td>+{r.phone}</td>
                    <td>{r.kind}</td>
                    <td style={{ maxWidth: 320 }}>
                      {r.body ? (
                        <span title={r.body}>
                          {r.body.length > 60 ? `${r.body.slice(0, 60)}…` : r.body}
                        </span>
                      ) : (
                        <span className="muted">код не сохраняется</span>
                      )}
                    </td>
                    <td className="num">{r.segments}</td>
                    <td>
                      <span style={{ color: statusColor(r.status) }}>
                        {STATUS_LABEL[r.status]}
                      </span>
                      {r.error && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {r.error}
                          {r.providerCode ? ` (код ${r.providerCode})` : ''}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div
              style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}
            >
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Назад
              </Button>
              <span className="muted">
                Страница {page + 1} из {pages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page + 1 >= pages}
                onClick={() => setPage(page + 1)}
              >
                Вперёд
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ═══════════════════════════ Рассылки ═══════════════════════════

const CAMPAIGN_STATUS: Record<Campaign['status'], { label: string; color: string }> = {
  pending: { label: 'Ожидает', color: 'var(--amber)' },
  running: { label: 'Идёт', color: 'var(--amber)' },
  done: { label: 'Завершена', color: 'var(--green)' },
  cancelled: { label: 'Отменена', color: 'var(--muted)' },
  failed: { label: 'Сбой', color: 'var(--red)' }
}

function CampaignsTool(): React.JSX.Element {
  const query = useConfigQuery()
  const clubId = (query?.clubId as string | undefined) ?? null
  const list = useFetch<CampaignsResponse>('/v1/sms/campaigns', query, [clubId])
  const [creating, setCreating] = useState(false)
  const [cancelling, setCancelling] = useState<Campaign | null>(null)
  const cancel = useMutate()

  const hasRunning = (list.data?.items ?? []).some((c) => c.status === 'running')

  // Пока рассылка идёт, обновляем прогресс: без этого счётчики «0 из 240»
  // выглядят так, будто отправка встала. Зависим от reload (он стабилен), а не от
  // самого list: у объекта запроса новая ссылка на каждый рендер, и интервал
  // пересоздавался бы бесконечно.
  const reload = list.reload
  useEffect(() => {
    if (!hasRunning) return
    const id = setInterval(() => reload(), 3000)
    return () => clearInterval(id)
  }, [hasRunning, reload])

  const onCancel = async (): Promise<void> => {
    if (!cancelling) return
    const ok = await cancel.mutate(() =>
      api.post(`/v1/sms/campaigns/${cancelling.id}/cancel`, {}, { query })
    )
    if (ok) {
      setCancelling(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="card">
        <SectionHead
          title="Рассылки"
          meta={
            list.data ? `${count(list.data.audienceSize)} клиентов с телефоном` : undefined
          }
        />

        {list.data && !list.data.limits.campaignsEnabled && (
          <div className="state state--error" role="alert" style={{ marginBottom: 16 }}>
            <span>⚠</span>
            <span>
              Массовые рассылки для клуба отключены администратором платформы. Транзакционные SMS
              (коды и подтверждения брони) продолжают работать.
            </span>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Button
            onClick={() => setCreating(true)}
            disabled={!list.data?.limits.campaignsEnabled || hasRunning}
          >
            Новая рассылка
          </Button>
          {hasRunning && (
            <span className="muted" style={{ marginLeft: 12 }}>
              Дождитесь окончания текущей рассылки.
            </span>
          )}
        </div>

        <AsyncState
          query={list}
          isEmpty={(list.data?.items ?? []).length === 0}
          empty="Рассылок ещё не было."
        />

        {list.data && list.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Создана</th>
                  <th className="num">Получателей</th>
                  <th className="num">Отправлено</th>
                  <th className="num">Ошибок</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div>{c.title}</div>
                      <div className="muted" style={{ fontSize: 12, maxWidth: 320 }}>
                        {c.body.length > 70 ? `${c.body.slice(0, 70)}…` : c.body}
                      </div>
                    </td>
                    <td>{dateTime(c.createdAt)}</td>
                    <td className="num">{count(c.total)}</td>
                    <td className="num">{count(c.sent)}</td>
                    <td className="num">
                      {c.failed > 0 ? (
                        <span style={{ color: 'var(--red)' }}>{count(c.failed)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span style={{ color: CAMPAIGN_STATUS[c.status].color }}>
                        {CAMPAIGN_STATUS[c.status].label}
                      </span>
                      {c.error && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {c.error}
                        </div>
                      )}
                    </td>
                    <td>
                      {c.status === 'running' && (
                        <Button variant="ghost" size="sm" onClick={() => setCancelling(c)}>
                          Отменить
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OptOutsCard />

      {creating && (
        <CampaignForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            list.reload()
          }}
        />
      )}
      {cancelling && (
        <ConfirmDialog
          title="Отменить рассылку?"
          message={`Уже отправленные сообщения вернуть нельзя. Отправка остановится на ближайшей пачке (отправлено ${cancelling.sent} из ${cancelling.total}).`}
          confirmLabel="Отменить рассылку"
          busy={cancel.saving}
          error={cancel.error}
          onConfirm={onCancel}
          onCancel={() => setCancelling(null)}
        />
      )}
    </>
  )
}

/** Создание рассылки: сначала предпросмотр аудитории и цены, потом отправка. */
function CampaignForm({
  onClose,
  onCreated
}: {
  onClose: () => void
  onCreated: () => void
}): React.JSX.Element {
  const query = useConfigQuery()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [levelId, setLevelId] = useState('')
  const [activeWithinDays, setActiveWithinDays] = useState('')
  const [preview, setPreview] = useState<CampaignPreview | null>(null)
  const previewCall = useMutate()
  const send = useMutate()

  const levels = useFetch<LoyaltyLevel[]>('/v1/loyalty/levels', query, [])

  const audience = useMemo(() => {
    const a: Record<string, unknown> = {}
    if (levelId) a.levelId = levelId
    if (activeWithinDays) a.activeWithinDays = Number(activeWithinDays)
    return Object.keys(a).length > 0 ? a : undefined
  }, [levelId, activeWithinDays])

  // Любое изменение содержимого обесценивает расчёт — иначе можно отправить,
  // глядя на цену предыдущей версии текста.
  useEffect(() => setPreview(null), [title, body, levelId, activeWithinDays])

  const onPreview = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const res = await previewCall.mutate(async () => {
      const data = await api.post<CampaignPreview>(
        '/v1/sms/campaigns/preview',
        { title, body, audience },
        { query }
      )
      setPreview(data)
      return data
    })
    if (!res) setPreview(null)
  }

  const onSend = async (): Promise<void> => {
    const ok = await send.mutate(() =>
      api.post('/v1/sms/campaigns', { title, body, audience }, { query })
    )
    if (ok) onCreated()
  }

  const tooMany = preview != null && preview.recipients > preview.maxRecipients

  return (
    <Modal
      title="Новая рассылка"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          {/* Отправка доступна только после расчёта: цену рекламы клуб должен
              увидеть до того, как она уйдёт, а не из счёта в конце месяца. */}
          {preview ? (
            <Button
              variant="primary"
              busy={send.saving}
              onClick={() => void onSend()}
              disabled={send.saving || tooMany || preview.recipients === 0}
            >
              {`Отправить ${count(preview.recipients)} сообщений`}
            </Button>
          ) : (
            <Button
              variant="primary"
              busy={previewCall.saving}
              onClick={(e) => void onPreview(e)}
              disabled={previewCall.saving || !title.trim() || !body.trim()}
            >
              Рассчитать
            </Button>
          )}
        </>
      }
    >
      <form className="form-grid" onSubmit={onPreview}>
        <Field label="Название (не отправляется клиентам)" htmlFor="camp-title" full>
          <Input
            id="camp-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Акция «Счастливые часы»"
            maxLength={120}
            required
          />
        </Field>

        <Field label="Текст сообщения" htmlFor="camp-body" full>
          <textarea
            id="camp-body"
            className="input"
            rows={4}
            value={body}
            maxLength={480}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Только в среду — час игры за полцены. Ждём!"
            required
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {body.length} символов. Кириллица режется на сегменты по 70 символов (в составном
            сообщении — по 67), и каждый сегмент тарифицируется отдельно. Подпись об отписке
            добавляется автоматически, если её нет в тексте.
          </div>
        </Field>

        <Field label="Уровень лояльности" htmlFor="camp-level">
          <Select id="camp-level" value={levelId} onChange={(e) => setLevelId(e.target.value)}>
            <option value="">Все клиенты</option>
            {(levels.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Играли за последние (дней)" htmlFor="camp-active">
          <Input
            id="camp-active"
            type="number"
            min={1}
            max={3650}
            value={activeWithinDays}
            onChange={(e) => setActiveWithinDays(e.target.value)}
            placeholder="без ограничения"
          />
        </Field>
      </form>

      {previewCall.error && (
        <div className="state state--error" role="alert">
          <span>⚠</span>
          <span>{previewCall.error}</span>
        </div>
      )}

      {preview && (
        <div className="card" style={{ marginTop: 16 }}>
          <SectionHead title="Что будет отправлено" />
          <div className="kpi-grid">
            <Kpi label="Получателей" value={count(preview.recipients)} />
            <Kpi
              label="Сегментов"
              value={count(preview.totalSegments)}
              sub={`${preview.segmentsPerMessage} на сообщение`}
            />
            <Kpi
              label="Бесплатный остаток"
              value={count(preview.freeRemaining)}
              sub="сегментов до конца месяца"
            />
            <Kpi
              label="К доплате"
              value={
                <>
                  {money(preview.estimatedCost)} <Cur />
                </>
              }
              sub={`${count(preview.chargedSegments)} сегм. сверх порога`}
              accent={preview.estimatedCost > 0}
            />
          </div>

          <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            Не попадут в рассылку: {count(preview.skippedOptOut)} отписавшихся,{' '}
            {count(preview.skippedNoPhone)} без телефона.
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="field__label">Текст, который получит клиент</div>
            <div className="input" style={{ whiteSpace: 'pre-wrap', background: 'transparent' }}>
              {preview.preparedBody}
            </div>
          </div>

          {tooMany && (
            <div className="state state--error" role="alert" style={{ marginTop: 12 }}>
              <span>⚠</span>
              <span>
                Получателей больше лимита ({count(preview.recipients)} при лимите{' '}
                {count(preview.maxRecipients)}). Сузьте условия или обратитесь к администратору
                платформы.
              </span>
            </div>
          )}
        </div>
      )}

      {send.error && (
        <div className="state state--error" role="alert" style={{ marginTop: 12 }}>
          <span>⚠</span>
          <span>{send.error}</span>
        </div>
      )}
    </Modal>
  )
}

/** Стоп-лист: кому рассылки не уходят. */
function OptOutsCard(): React.JSX.Element {
  const query = useConfigQuery()
  const clubId = (query?.clubId as string | undefined) ?? null
  const list = useFetch<OptOut[]>('/v1/sms/opt-outs', query, [clubId])
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const add = useMutate()
  const remove = useMutate()

  const onAdd = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const ok = await add.mutate(() =>
      api.post('/v1/sms/opt-outs', { phone, note: note || undefined }, { query })
    )
    if (ok) {
      setPhone('')
      setNote('')
      list.reload()
    }
  }

  const onRemove = async (p: string): Promise<void> => {
    const ok = await remove.mutate(() =>
      api.del(`/v1/sms/opt-outs/${encodeURIComponent(p)}`, { query })
    )
    if (ok) list.reload()
  }

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead
        title="Стоп-лист рассылок"
        meta={list.data ? `${count(list.data.length)} номеров` : undefined}
      />
      <p className="muted" style={{ marginTop: 0 }}>
        Эти номера не получают массовые рассылки. Коды подтверждения и подтверждения брони им
        по-прежнему приходят — это ответ на действие самого клиента, а не реклама.
      </p>

      <form className="report-form" onSubmit={onAdd}>
        <Field label="Номер" htmlFor="opt-phone">
          <Input
            id="opt-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+375 29 123-45-67"
            required
          />
        </Field>
        <Field label="Примечание" htmlFor="opt-note">
          <Input
            id="opt-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="попросил не писать"
          />
        </Field>
        <div style={{ alignSelf: 'end' }}>
          <Button type="submit" disabled={add.saving || !phone.trim()}>
            Добавить
          </Button>
        </div>
      </form>

      {add.error && (
        <div className="state state--error" role="alert">
          <span>⚠</span>
          <span>{add.error}</span>
        </div>
      )}

      <AsyncState
        query={list}
        isEmpty={(list.data ?? []).length === 0}
        empty="Стоп-лист пуст."
      />

      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Номер</th>
                <th>Примечание</th>
                <th>Добавлен</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.map((o) => (
                <tr key={o.id}>
                  <td>+{o.phone}</td>
                  <td>{o.note ?? '—'}</td>
                  <td>{dateTime(o.createdAt)}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={remove.saving}
                      onClick={() => onRemove(o.phone)}
                    >
                      Убрать
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════ Проверка шлюза ═══════════════════════════

interface SmsStatusInfo {
  dryRun: boolean
  credsSource: 'club' | 'env' | 'none'
  originator: string | null
  message: string
}

interface TestResult {
  ok: boolean
  dryRun: boolean
  providerCode: string | null
  error: string | null
  segments: number
  msisdn: string
}

function SelfTestTool(): React.JSX.Element {
  const query = useConfigQuery()
  const clubId = (query?.clubId as string | undefined) ?? null
  const { isSuperAdmin } = useClubContext()
  const status = useFetch<SmsStatusInfo>('/v1/sms/status', query, [clubId])
  const [phone, setPhone] = useState('')
  const [text, setText] = useState('')
  const [force, setForce] = useState(false)
  const [result, setResult] = useState<TestResult | null>(null)
  const send = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setResult(null)
    await send.mutate(async () => {
      const data = await api.post<TestResult>(
        '/v1/sms/test',
        { phone, text: text || undefined, force: force || undefined },
        { query }
      )
      setResult(data)
      status.reload()
      return data
    })
  }

  return (
    <div className="card">
      <SectionHead title="Проверка SMS-шлюза" />

      <AsyncState query={status} />
      {status.data && (
        <div
          className="state"
          style={{ marginBottom: 16, color: status.data.dryRun ? 'var(--amber)' : undefined }}
        >
          <span>{status.data.dryRun ? '⚠' : 'ℹ'}</span>
          <span>
            {status.data.message}
            {status.data.originator ? ` Отправитель: ${status.data.originator}.` : ''}
          </span>
        </div>
      )}

      <form className="report-form" onSubmit={onSubmit}>
        <Field label="Номер получателя" htmlFor="test-phone">
          <Input
            id="test-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+375 29 123-45-67"
            required
          />
        </Field>
        <Field label="Текст (необязательно)" htmlFor="test-text">
          <Input
            id="test-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Проверка SMS-шлюза 8Ball POS."
            maxLength={200}
          />
        </Field>
        <div style={{ alignSelf: 'end' }}>
          <Button type="submit" disabled={send.saving || !phone.trim()}>
            {send.saving ? 'Отправляем…' : 'Отправить'}
          </Button>
        </div>
      </form>

      {isSuperAdmin && status.data?.dryRun && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          <span>
            Отправить по-настоящему в обход тестового режима (только администратор платформы)
          </span>
        </label>
      )}

      {send.error && (
        <div className="state state--error" role="alert" style={{ marginTop: 12 }}>
          <span>⚠</span>
          <span>{send.error}</span>
        </div>
      )}

      {result && (
        <div
          className={result.ok ? 'state' : 'state state--error'}
          role="status"
          style={{ marginTop: 12 }}
        >
          <span>{result.ok ? '✓' : '⚠'}</span>
          <span>
            {result.dryRun
              ? `Тестовый режим: сообщение на +${result.msisdn} записано в лог, но не отправлено.`
              : result.ok
                ? `Шлюз принял сообщение на +${result.msisdn} (${result.segments} сегм.).`
                : `Шлюз отклонил отправку: ${result.error ?? 'без описания'}${
                    result.providerCode ? ` (код ${result.providerCode})` : ''
                  }`}
          </span>
        </div>
      )}
    </div>
  )
}

// ═══════════════════ Обзор платформы (super_admin) ═══════════════════

function PlatformOverviewTool(): React.JSX.Element {
  const period = usePeriod()
  const overview = useFetch<PlatformOverview>(
    '/v1/sms/stats/overview',
    { from: period.from, to: period.to },
    [period.from, period.to]
  )

  return (
    <>
      <div className="card">
        <SectionHead title="Период" />
        <form className="report-form" onSubmit={(e) => e.preventDefault()}>
          {period.controls}
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <SectionHead
          title="Итого по платформе"
          meta={
            overview.data ? (
              <>
                превышение текущего месяца: {money(overview.data.monthAmount)} <Cur />
              </>
            ) : undefined
          }
        />
        <AsyncState query={overview} />
        {overview.data && (
          <>
            <TotalsKpi totals={overview.data.totals} />
            <div style={{ marginTop: 24 }}>
              <SectionHead title="По видам сообщений" />
              <KindTable rows={overview.data.byKind} />
            </div>
          </>
        )}
      </div>

      {overview.data && (
        <div className="card" style={{ marginTop: 24 }}>
          <SectionHead
            title="По заведениям"
            meta={`${count(overview.data.clubs.length)} клубов с отправками`}
          />
          {overview.data.clubs.length === 0 ? (
            <EmptyState message="За период ни один клуб не отправлял SMS." />
          ) : (
            <div className="table-wrap">
              <table className="rtable">
                <thead>
                  <tr>
                    <th>Заведение</th>
                    <th className="num">Всего</th>
                    <th className="num">Успешно</th>
                    <th className="num">Ошибок</th>
                    <th className="num">Сегментов</th>
                    <th className="num">Месяц: сегм.</th>
                    <th className="num">Порог</th>
                    <th className="num">К оплате</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.data.clubs.map((c) => (
                    <tr key={c.clubId}>
                      <td>
                        {c.clubName}
                        {c.isTest && (
                          <span className="badge" style={{ marginLeft: 8 }}>
                            тестовый
                          </span>
                        )}
                      </td>
                      <td className="num">{count(c.messages)}</td>
                      <td className="num">{count(c.sent)}</td>
                      <td className="num">
                        {c.failed > 0 ? (
                          <span style={{ color: 'var(--red)' }}>{count(c.failed)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="num">{count(c.billableSegments)}</td>
                      <td className="num">{count(c.monthBillableSegments)}</td>
                      <td className="num">{count(c.freeSegments)}</td>
                      <td className="num">
                        {c.amount > 0 ? (
                          <span style={{ color: 'var(--amber)' }}>
                            {money(c.amount)} <Cur />
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ═══════════════════ Тарификация (super_admin) ═══════════════════

function PricingTool(): React.JSX.Element {
  return (
    <>
      <DefaultsForm />
      <ClubGate>
        <ClubLimitsForm />
      </ClubGate>
    </>
  )
}

/** Общий прейскурант: действует для всех клубов без переопределения. */
function DefaultsForm(): React.JSX.Element {
  const defaults = useFetch<BillingDefaults>('/v1/sms/billing/defaults')
  const save = useMutate()
  const [free, setFree] = useState('')
  const [price, setPrice] = useState('')
  const [maxRecipients, setMaxRecipients] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!defaults.data) return
    setFree(String(defaults.data.freeSegments))
    setPrice(String(defaults.data.pricePerSegment))
    setMaxRecipients(String(defaults.data.campaignMaxRecipients))
  }, [defaults.data])

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setSaved(false)
    const ok = await save.mutate(() =>
      api.put('/v1/sms/billing/defaults', {
        freeSegments: Number(free),
        pricePerSegment: Number(price),
        campaignMaxRecipients: Number(maxRecipients)
      })
    )
    if (ok) {
      setSaved(true)
      defaults.reload()
    }
  }

  return (
    <div className="card">
      <SectionHead title="Общий прейскурант" />
      <p className="muted" style={{ marginTop: 0 }}>
        Действует для всех клубов, у которых нет собственных значений. Порог считается по
        сегментам за календарный месяц: длинное сообщение — это несколько сегментов, и оператор
        берёт деньги за каждый.
      </p>

      <AsyncState query={defaults} />
      {defaults.data && (
        <form className="report-form" onSubmit={onSubmit}>
          <Field label="Бесплатно сегментов в месяц" htmlFor="def-free">
            <Input
              id="def-free"
              type="number"
              min={0}
              value={free}
              onChange={(e) => setFree(e.target.value)}
              required
            />
          </Field>
          <Field label="Цена сегмента сверх порога, BYN" htmlFor="def-price">
            <Input
              id="def-price"
              type="number"
              min={0}
              step="0.0001"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </Field>
          <Field label="Потолок одной рассылки" htmlFor="def-max">
            <Input
              id="def-max"
              type="number"
              min={1}
              value={maxRecipients}
              onChange={(e) => setMaxRecipients(e.target.value)}
              required
            />
          </Field>
          <div style={{ alignSelf: 'end' }}>
            <Button type="submit" disabled={save.saving}>
              {save.saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </form>
      )}

      {save.error && (
        <div className="state state--error" role="alert">
          <span>⚠</span>
          <span>{save.error}</span>
        </div>
      )}
      {saved && !save.error && (
        <div className="state" role="status">
          <span>✓</span>
          <span>Прейскурант сохранён.</span>
        </div>
      )}
    </div>
  )
}

/** Переопределение для выбранного клуба: пустое поле = общий прейскурант. */
function ClubLimitsForm(): React.JSX.Element {
  const { clubId, clubs } = useClubContext()
  const clubName = clubs.find((c) => c.id === clubId)?.name ?? null
  const query = clubId ? { clubId } : undefined
  const data = useFetch<ClubLimitsResponse>('/v1/sms/billing/club', query, [clubId])
  const save = useMutate()
  const [free, setFree] = useState('')
  const [price, setPrice] = useState('')
  const [maxRecipients, setMaxRecipients] = useState('')
  const [campaignsEnabled, setCampaignsEnabled] = useState(true)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const o = data.data?.override
    setFree(o?.freeSegments != null ? String(o.freeSegments) : '')
    setPrice(o?.pricePerSegment != null ? String(o.pricePerSegment) : '')
    setMaxRecipients(o?.campaignMaxRecipients != null ? String(o.campaignMaxRecipients) : '')
    setCampaignsEnabled(o?.campaignsEnabled ?? true)
    setSaved(false)
  }, [data.data])

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setSaved(false)
    const ok = await save.mutate(() =>
      api.put(
        '/v1/sms/billing/club',
        {
          // Пустая строка = сброс к общему прейскуранту, поэтому именно null,
          // а не пропуск поля: иначе значение осталось бы старым.
          freeSegments: free === '' ? null : Number(free),
          pricePerSegment: price === '' ? null : Number(price),
          campaignMaxRecipients: maxRecipients === '' ? null : Number(maxRecipients),
          campaignsEnabled
        },
        { query }
      )
    )
    if (ok) {
      setSaved(true)
      data.reload()
    }
  }

  const eff = data.data?.effective

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead
        title={`Индивидуально: ${clubName ?? 'клуб'}`}
        meta={
          eff
            ? `сейчас действует: ${count(eff.freeSegments)} сегм. и ${money(eff.pricePerSegment)} за сегмент`
            : undefined
        }
      />
      <p className="muted" style={{ marginTop: 0 }}>
        Пустое поле — клуб живёт по общему прейскуранту. Заполненное перекрывает его.
      </p>

      <AsyncState query={data} />
      {data.data && (
        <form className="report-form" onSubmit={onSubmit}>
          <Field label="Бесплатно сегментов в месяц" htmlFor="club-free">
            <Input
              id="club-free"
              type="number"
              min={0}
              value={free}
              onChange={(e) => setFree(e.target.value)}
              placeholder="по общему прейскуранту"
            />
          </Field>
          <Field label="Цена сегмента, BYN" htmlFor="club-price">
            <Input
              id="club-price"
              type="number"
              min={0}
              step="0.0001"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="по общему прейскуранту"
            />
          </Field>
          <Field label="Потолок одной рассылки" htmlFor="club-max">
            <Input
              id="club-max"
              type="number"
              min={1}
              value={maxRecipients}
              onChange={(e) => setMaxRecipients(e.target.value)}
              placeholder="по общему прейскуранту"
            />
          </Field>
          <div style={{ alignSelf: 'end', display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={campaignsEnabled}
                onChange={(e) => setCampaignsEnabled(e.target.checked)}
              />
              <span>Разрешить рассылки</span>
            </label>
            <Button type="submit" disabled={save.saving}>
              {save.saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </form>
      )}

      {save.error && (
        <div className="state state--error" role="alert">
          <span>⚠</span>
          <span>{save.error}</span>
        </div>
      )}
      {saved && !save.error && (
        <div className="state" role="status">
          <span>✓</span>
          <span>Настройки клуба сохранены.</span>
        </div>
      )}
    </div>
  )
}
